import React, { useEffect, useState, useCallback } from "react";
import { Cell } from "@components/table/types";
import { Form } from "@components";
import { Field } from "../../../../../../components/form/fields/field";
import { Table } from "@components";
import { RoleApproverModel } from "@api/types/role";
import { Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { useApproverDistroSelect } from "@hooks";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import api from "@api";
import DeleteIcon from "@material-ui/icons/Delete";
import { alertService } from "@services";
import Hooks from "@hooks";
import { ConfirmationModal } from "../../../../../request/steps/summary/views/summary/confirmation-modal";

interface Props {
  form: Manager<RoleApproverModel>;
}

type Key = keyof RoleApproverModel;

const headers: Cell[] = [
  { data: "Environment" },
  { data: "Approver 1 Level" },
  { data: "Approver 2 Level" },
  { data: "Approver 2 Email" },
  { data: "Approver 3 Level" },
  { data: "Approver 3 Email" },
  { data: "Approver 4 Level" },
  { data: "Approver 4 Email" },
  { data: "Approver Distro Level 2" },
  { data: "Approver Distro Level 3" },
  { data: "Approver Distro Level 4" },
  { data: "" },
];

export const RoleApprover: React.FC<Props> = ({ form }) => {
  const roleApprover: RoleApproverModel = {
    id: 0,
    environment: "",
    approver2Email: "",
    approver3Email: "",
    approver4Email: "",
    approver1Level: "Manager",
    approver2Level: "",
    approver3Level: "",
    approver4Level: "",
    approverDistro2: null,
    approverDistro3: null,
    approverDistro4: null,
    role: 0,
    systemAppId: 0,
  };

  // custom hooks
  const {
    approverDistros,
    isFetchingApproverDistros,
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
    setSelectedApproverDistro2,
    setSelectedApproverDistro3,
    setSelectedApproverDistro4,
    handleApproverDistroTypeChange,
  } = useApproverDistroSelect(
    null,
    form.values.approverDistro2,
    form.values.approverDistro3,
    form.values.approverDistro4
  );

  const [selectedRole, setSelectedRole] = useState(0);
  const [isConfirming, setConfirmingState] = useState<boolean>(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState<number|null>(null);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [selectedAppId, setSelectedAppId] = useState(0);

  useEffect(() => {
    if (!selectedRole) {
      setSelectedRole(form.values.role);
    }
  }, [form.values.role]);

  useEffect(() => {
    if (!selectedAppId) {
      setSelectedAppId(form.values.systemAppId);
    }
  }, [form.values.systemAppId]);

  const [environments, ,] = Hooks.useEnvironments(undefined);

  const environmentOptions = React.useMemo(() => {
    return (environments || [])
      .filter((environment) => environment.isActive === true)
      .map(({ name }) => `${name}`);
  }, [environments]);

  const approverLevels = [
    {
      label: "No Selection",
      value: "",
    },
    {
      label: "Manager",
      value: "Manager",
    },
    {
      label: "Second Manager",
      value: "Second Manager",
    },
    {
      label: "VP",
      value: "VP",
    },
    {
      label: "SVP",
      value: "SVP",
    },
  ];

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
    if (name === "approver2Level") {
      form.set(Form.target.VALUE, ["approver2Email", ""]);
      setSelectedApproverDistro2(0);
    }
    if (name === "approver3Level") {
      form.set(Form.target.VALUE, ["approver3Email", ""]);
      setSelectedApproverDistro3(0);
    }
    if (name === "approver4Level") {
      form.set(Form.target.VALUE, ["approver4Email", ""]);
      setSelectedApproverDistro4(0);
    }
    if (name === "approver2Email") {
      form.set(Form.target.VALUE, ["approver2Level", ""]);
      setSelectedApproverDistro2(0);
    }
    if (name === "approver3Email") {
      form.set(Form.target.VALUE, ["approver3Level", ""]);
      setSelectedApproverDistro3(0);
    }
    if (name === "approver4Email") {
      form.set(Form.target.VALUE, ["approver4Level", ""]);
      setSelectedApproverDistro4(0);
    }
  }

  useEffect(() => {
    if (selectedApproverDistro2) {
      form.set(Form.target.VALUE, ["approver2Email", ""]);
      form.set(Form.target.VALUE, ["approver2Level", ""]);
    }
    if (selectedApproverDistro3) {
      form.set(Form.target.VALUE, ["approver3Email", ""]);
      form.set(Form.target.VALUE, ["approver3Level", ""]);
    }
    if (selectedApproverDistro4) {
      form.set(Form.target.VALUE, ["approver4Email", ""]);
      form.set(Form.target.VALUE, ["approver4Level", ""]);
    }
  }, [
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
  ]);

  const [roleApprovers, setRoleApprovers] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  useEffect(() => {
    getRoleApproverTable();
  }, [form.values.role]);

  function getRoleApproverTable() {
    const role = form.values.role;
    if (role) {
      setIsLoadingTable(true);
      api.maintenancerole
        .getRoleApproversByRoleId(role)
        .then((response) => {
          setRoleApprovers(response);
        })
        .catch((error) => {
          console.log(error);
        })
        .then(() => {
          setIsLoadingTable(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "environment":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "approver2Email":
        if (form.values.approver2Email) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        } else {
          return Promise.resolve([name as Key]);
        }
      case "approver3Email":
        if (form.values.approver3Email) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        } else {
          return Promise.resolve([name as Key]);
        }
      case "approver4Email":
        if (form.values.approver4Email) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        }
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function clearRoleApproverModel() {
    roleApprover.id = 0;
    roleApprover.environment = "";
    roleApprover.approver1Level = "Manager";
    roleApprover.approver2Level = "";
    roleApprover.approver3Level = "";
    roleApprover.approver4Level = "";
    roleApprover.approver2Email = "";
    roleApprover.approver3Email = "";
    roleApprover.approver4Email = "";
    roleApprover.approverDistro2 = null;
    roleApprover.approverDistro3 = null;
    roleApprover.approverDistro4 = null;
    form.reset();
    setSelectedApproverDistro2(0);
    setSelectedApproverDistro3(0);
    setSelectedApproverDistro4(0);
    // re-assign the selected role
    form.set(Form.target.VALUE, ["role", selectedRole]);
    form.set(Form.target.VALUE, ["systemAppId", selectedAppId]);
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitApprover();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  const [isSavingApprover, setIsSavingApprover] = useState(false);
  function submitApprover() {
    setIsSavingApprover(true);
    roleApprover.id = form.values.id;
    roleApprover.environment = form.values.environment;
    roleApprover.approver1Level = form.values.approver1Level;
    roleApprover.approver2Level = form.values.approver2Level;
    roleApprover.approver3Level = form.values.approver3Level;
    roleApprover.approver4Level = form.values.approver4Level;
    roleApprover.approver2Email = form.values.approver2Email;
    roleApprover.approver3Email = form.values.approver3Email;
    roleApprover.approver4Email = form.values.approver4Email;
    roleApprover.approverDistro2 = selectedApproverDistro2
      ? selectedApproverDistro2
      : null;
    roleApprover.approverDistro3 = selectedApproverDistro3
      ? selectedApproverDistro3
      : null;
    roleApprover.approverDistro4 = selectedApproverDistro4
      ? selectedApproverDistro4
      : null;
    roleApprover.role = selectedRole;
    roleApprover.systemAppId = form.values.systemAppId;
    if (roleApprover.id != 0) {
      // UPDATE
      api.maintenancerole
        .putRoleApprover(roleApprover.id, roleApprover)
        .then((response) => {
          if (response) {
            alertService.success({ msg: "Role approver has been updated." });
            getRoleApproverTable();
            clearRoleApproverModel();
          } else {
            alertService.danger({ msg: "Failed to Update role approver" });
          }
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setIsSavingApprover(false);
        });
    } else {
      // CREATE
      api.maintenancerole
        .postRoleApprover(roleApprover)
        .then((response) => {
          if (response) {
            alertService.success({ msg: "Role approver has been added." });
            getRoleApproverTable();
            clearRoleApproverModel();
          } else {
            alertService.danger({ msg: "Failed to add role approver." });
          }
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setIsSavingApprover(false);
        });
    }
  }
  
  function handleConfirmationCancel() {
    closeConfirmationModal()
  }

  function handleConfirmationConfirm() {
    setConfirmingState(true);
    confirmDeleteApprover(idToBeDeleted);
  }

  function openConfirmationModal(id: number)
  {
    setModalMessage("Are you sure do you want to remove this data?");
    setIdToBeDeleted(id);
    setConfirmingState(true);
  }

  function closeConfirmationModal()
  {
    setModalMessage("");
    setIdToBeDeleted(null);
    setConfirmingState(false);
  }

  function confirmDeleteApprover(id: number|null) {
    if (id) {
      api.maintenancerole
        .deleteRoleApproverById(id)
        .then((response) => {
          if (response) {
            getRoleApproverTable();
            alertService.success({
              msg: "Role approver has been successfully deleted",
            });
          } else {
            alertService.danger({
              msg: "Something went wrong. Failed to delete.",
            });
          }
          closeConfirmationModal()
        })
        .catch((error) => {
          console.log(error);
          closeConfirmationModal()
        });
    }
  }

  function selectApprover(data: any) {
    // reset existing or selected data in the form
    form.reset();

    form.set(Form.target.VALUE, ["id", data.id]);
    form.set(Form.target.VALUE, ["environment", data.environment]);
    form.set(Form.target.VALUE, ["approver1Level", data.approver1Level]);
    form.set(Form.target.VALUE, ["approver2Email", data.approver2Email]);
    form.set(Form.target.VALUE, ["approver2Level", data.approver2Level]);
    form.set(Form.target.VALUE, ["approver3Email", data.approver3Email]);
    form.set(Form.target.VALUE, ["approver3Level", data.approver3Level]);
    form.set(Form.target.VALUE, ["approver4Email", data.approver4Email]);
    form.set(Form.target.VALUE, ["approver4Level", data.approver4Level]);
    form.set(Form.target.VALUE, ["approverDistro2", data.approverDistro2]);
    setSelectedApproverDistro2(data.approverDistro2 ? data.approverDistro2 : 0);
    setSelectedApproverDistro3(data.approverDistro3 ? data.approverDistro3 : 0);
    setSelectedApproverDistro4(data.approverDistro4 ? data.approverDistro4 : 0);
    form.set(Form.target.VALUE, ["approverDistro3", data.approverDistro3]);
    form.set(Form.target.VALUE, ["approverDistro4", data.approverDistro4]);
    form.set(Form.target.VALUE, ["role", data.role]);
    form.set(Form.target.VALUE, ["systemAppId", data.systemAppId]);
  }

  let approverDistroMenu = null;
  if (isFetchingApproverDistros) {
    approverDistroMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    approverDistroMenu = approverDistros.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let submitApproverButtonText = null;
  let actionLabel = null;
  if (isSavingApprover) {
    submitApproverButtonText = "Saving...";
  } else {
    if (form.values.id) {
      submitApproverButtonText = "Update";
      actionLabel = "Update";
    } else {
      submitApproverButtonText = "+ Add";
      actionLabel = "Add";
    }
  }

  const deleteIcon = (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <DeleteIcon />
    </span>
  );

  function cancelSelectedApprover() {
    clearRoleApproverModel();
  }

  let cancelSelectedApproverButton = null;
  if (form.values.id) {
    cancelSelectedApproverButton = (
      <Button variant="outlined" type="button" onClick={cancelSelectedApprover}>
        Cancel
      </Button>
    );
  } else {
    cancelSelectedApproverButton = null;
  }

  return (
    <div>
      <h4>{actionLabel} Role Approval</h4>
      <hr />
      <h5>1. {actionLabel} Role Environment</h5>
      <Form>
        <Form.Dropdown
          label="Environment"
          onChange={onChange}
          options={[
            { label: "No Selection", value: "" },
            ...environmentOptions,
          ]}
          name="environment"
          value={form.values.environment}
          error={form.errors.environment}
          size={4}
        />
      </Form>
      <h5>2. Role Approvers</h5>
      <Form>
        <Form.Textbox
          label="Approver 1 Level"
          name="approver1Level"
          onChange={onChange}
          size={6}
          value={form.values.approver1Level}
          disabled
        />
        <Field label="" size={6}></Field>
        <Form.Dropdown
          label="Approver 2 Level"
          onChange={onChange}
          options={approverLevels}
          name="approver2Level"
          value={form.values.approver2Level}
          size={6}
        />
        <Form.Textbox
          label="Approver 2 Email"
          name="approver2Email"
          onChange={onChange}
          size={6}
          error={form.errors.approver2Email}
          value={form.values.approver2Email}
        />
        <Form.Dropdown
          label="Approver 3 Level"
          onChange={onChange}
          options={approverLevels}
          name="approver3Level"
          value={form.values.approver3Level}
          size={6}
        />
        <Form.Textbox
          label="Approver 3 Email"
          name="approver3Email"
          onChange={onChange}
          size={6}
          error={form.errors.approver3Email}
          value={form.values.approver3Email}
        />
        <Form.Dropdown
          label="Approver 4 Level"
          onChange={onChange}
          options={approverLevels}
          name="approver4Level"
          value={form.values.approver4Level}
          size={6}
        />
        <Form.Textbox
          label="Approver 4 Email"
          name="approver4Email"
          onChange={onChange}
          size={6}
          error={form.errors.approver4Email}
          value={form.values.approver4Email}
        />
        <Field label="Approver Distro 2" size={6}>
          <Select
            name="approverDistro2"
            value={selectedApproverDistro2}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="" size={6}></Field>
        <Field label="Approver Distro 3" size={6}>
          <Select
            name="approverDistro3"
            value={selectedApproverDistro3}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="" size={6}></Field>
        <Field label="Approver Distro 4" size={6}>
          <Select
            name="approverDistro4"
            value={selectedApproverDistro4}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="" size={6}></Field>
        <Field label="" size={2}>
          <Button
            variant="outlined"
            type="button"
            onClick={handleSubmit}
            disabled={isSavingApprover}
          >
            {submitApproverButtonText}
          </Button>
        </Field>
        <Field label="" size={2}>
          {cancelSelectedApproverButton}
        </Field>
      </Form>
      <br />
      <Form>
        <Field label="" size={12}>
          <Table
            data={roleApprovers.map((data: any) => ({
              cells: [
                {
                  data: data.environment,
                  color: "blue",
                  cursor: "pointer",
                  onClick: () => {
                    selectApprover(data);
                  },
                },
                {
                  data: data.approver1Level,
                },
                {
                  data: data.approver2Level,
                },
                {
                  data: data.approver2Email,
                },
                {
                  data: data.approver3Level,
                },
                {
                  data: data.approver3Email,
                },
                {
                  data: data.approver4Level,
                },
                {
                  data: data.approver4Email,
                },
                {
                  data: data.approverDistro2Name,
                },
                {
                  data: data.approverDistro3Name,
                },
                {
                  data: data.approverDistro4Name,
                },
                {
                  data: deleteIcon,
                  color: "gray",
                  cursor: "pointer",
                  onClick: () => {
                    openConfirmationModal(data.id);
                  },
                },
              ],
              data: data,
            }))}
            tableName={"System / Application Name"}
            headers={headers}
            loading={isLoadingTable}
          />
        </Field>
        <ConfirmationModal
          onCancel={handleConfirmationCancel}
          onConfirm={handleConfirmationConfirm}
          open={isConfirming}
          customMessage={modalMessage}
        />
      </Form>
    </div>
  );
};
